/**
 * 相对时间格式化工具
 * 将 ISO 时间字符串转为中文相对时间（刚刚/5分钟前/1小时前/昨天/3天前）
 */
export function formatRelativeTime(dateStr: string): string {
  if (!dateStr) return '暂无联系'
  const parsed = new Date(dateStr)
  if (isNaN(parsed.getTime()) || parsed.getFullYear() <= 1970) return '暂无联系'
  const now = Date.now()
  const date = parsed.getTime()
  const diff = now - date

  if (diff < 60_000) return '刚刚'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}分钟前`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}小时前`
  if (diff < 172_800_000) return '昨天'
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}天前`
  return new Date(dateStr).toLocaleDateString('zh-CN')
}

/**
 * 格式化 Date 为本地时间字符串（YYYY-MM-DDTHH:mm）
 * 用于 datetime-local input
 */
export function formatToLocalDateTime(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const BEIJING_TIME_ZONE = 'Asia/Shanghai'

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const LOCAL_DATE_TIME_PATTERN =
  /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?)?$/
const TIMESTAMP_PATTERN = /^\d{10,13}$/

function isValidCalendarDate(year: number, month: number, day: number) {
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  )
}

function normalizeTimestamp(value: number): string {
  if (!Number.isFinite(value)) return ''
  const milliseconds = Math.abs(value) < 1e11 ? value * 1000 : value
  const date = new Date(milliseconds)
  return Number.isNaN(date.getTime()) ? '' : formatBeijingDate(date)
}

/**
 * Normalize values returned by date pickers or serialized by production builds
 * to the API's strict Beijing calendar date format (YYYY-MM-DD).
 */
export function normalizeBeijingDate(value: unknown): string {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : formatBeijingDate(value)
  }
  if (typeof value === 'number') return normalizeTimestamp(value)

  const text = String(value ?? '').trim()
  if (!text) return ''

  const dateOnly = text.match(DATE_ONLY_PATTERN)
  if (dateOnly) {
    const [, year, month, day] = dateOnly
    return isValidCalendarDate(Number(year), Number(month), Number(day)) ? text : ''
  }

  const localDateTime = text.match(LOCAL_DATE_TIME_PATTERN)
  if (localDateTime) {
    const [, dateText, hour, minute, second = '0'] = localDateTime
    const dateParts = dateText.match(DATE_ONLY_PATTERN)
    if (
      !dateParts ||
      !isValidCalendarDate(Number(dateParts[1]), Number(dateParts[2]), Number(dateParts[3])) ||
      Number(hour) > 23 ||
      Number(minute) > 59 ||
      Number(second) > 59
    ) {
      return ''
    }
    return dateText
  }

  if (TIMESTAMP_PATTERN.test(text)) return normalizeTimestamp(Number(text))

  const parsed = new Date(text)
  return Number.isNaN(parsed.getTime()) ? '' : formatBeijingDate(parsed)
}

/**
 * Format a date as Beijing calendar date (YYYY-MM-DD).
 */
export function formatBeijingDate(value: Date | number | string = new Date()): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: BEIJING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const year = parts.find((part) => part.type === 'year')?.value
  const month = parts.find((part) => part.type === 'month')?.value
  const day = parts.find((part) => part.type === 'day')?.value
  return year && month && day ? `${year}-${month}-${day}` : ''
}

/**
 * 格式化日期为北京时间字符串（YYYY-MM-DD HH:mm:ss）。
 * 参数 value 为后端返回的日期、时间戳或 Date 对象。
 */
export function formatBeijingDateTime(value: Date | number | string): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: BEIJING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value
  const [year, month, day, hour, minute, second] = [
    part('year'),
    part('month'),
    part('day'),
    part('hour'),
    part('minute'),
    part('second'),
  ]

  return year && month && day && hour && minute && second
    ? `${year}-${month}-${day} ${hour}:${minute}:${second}`
    : ''
}

/**
 * Format a date as a Windows/macOS-safe Beijing timestamp for exported filenames.
 */
export function formatBeijingFileTimestamp(value: Date | number | string = new Date()): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: BEIJING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value
  const [year, month, day, hour, minute, second] = [
    part('year'),
    part('month'),
    part('day'),
    part('hour'),
    part('minute'),
    part('second'),
  ]

  return year && month && day && hour && minute && second
    ? `${year}-${month}-${day}_${hour}-${minute}-${second}`
    : ''
}

/**
 * 获取默认时间范围（最近24小时，使用本地时间）
 * 用于审计日志等需要时间范围的筛选
 */
export function getDefaultTimeRange(): { startTime: string; endTime: string } {
  const now = new Date()
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
  return {
    startTime: formatToLocalDateTime(yesterday),
    endTime: formatToLocalDateTime(now),
  }
}

type DateRangeShortcutValue = () => [number, number]
export type DateRangeShortcuts = Record<string, DateRangeShortcutValue>

function startOfDay(date: Date) {
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)
  return target.getTime()
}

function endOfDay(date: Date) {
  const target = new Date(date)
  target.setHours(23, 59, 59, 999)
  return target.getTime()
}

function addDays(date: Date, days: number) {
  const target = new Date(date)
  target.setDate(target.getDate() + days)
  return target
}

function addMonths(date: Date, months: number) {
  const target = new Date(date)
  const currentDate = target.getDate()
  target.setDate(1)
  target.setMonth(target.getMonth() + months)
  const daysInTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(currentDate, daysInTargetMonth))
  return target
}

function rangeFromDates(startDate: Date, endDate: Date): [number, number] {
  return [startOfDay(startDate), endOfDay(endDate)]
}

/** 列表日期区间选择器统一使用的快捷日期范围。 */
export const defaultDateRangeShortcuts: DateRangeShortcuts = {
  今天: () => {
    const today = new Date()
    return rangeFromDates(today, today)
  },
  昨天: () => {
    const yesterday = addDays(new Date(), -1)
    return rangeFromDates(yesterday, yesterday)
  },
  近7天: () => {
    const today = new Date()
    return rangeFromDates(addDays(today, -6), today)
  },
  近30天: () => {
    const today = new Date()
    return rangeFromDates(addDays(today, -29), today)
  },
  本月: () => {
    const today = new Date()
    return rangeFromDates(new Date(today.getFullYear(), today.getMonth(), 1), today)
  },
  近半年: () => {
    const today = new Date()
    return rangeFromDates(addMonths(today, -6), today)
  },
  近一年: () => {
    const today = new Date()
    return rangeFromDates(addMonths(today, -12), today)
  },
}
